// frontend/src/screens/admin/sps/RunHistoryScreen.jsx
import { useState } from 'react';
import {
  Container, Row, Col, Table, Badge, Button, Form,
  Modal, Alert, Spinner, Pagination,
} from 'react-bootstrap';
import { useGetRunHistoryQuery, useGetRunDetailQuery } from '../../../slices/spsApiSlices';

function LogModal({ runId, onClose }) {
  const { data: run, isLoading } = useGetRunDetailQuery(runId, { skip: !runId });

  return (
    <Modal show={!!runId} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>
          Run Log — {run?.runType === 'batch' ? 'Batch' : run?.staffName}
          {run && <span className="text-muted ms-2" style={{ fontSize: '.82rem', fontFamily: 'monospace' }}>{run._id}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : run ? (
          <>
            <Row className="g-2 mb-3">
              {[
                ['Type',      run.runType],
                  ...(run.runType === 'batch'
                    ? [['Mode', run.resumed
                          ? <Badge bg="warning" text="dark">Resumed</Badge>
                          : <Badge bg="light">Fresh</Badge>]]
                    : []),
                ['Year',      run.runYear],
                ['Status',    <Badge bg={run.status==='completed'?'success':run.status==='error'?'danger':'warning'}>{run.status}</Badge>],
                ['Started',   new Date(run.startedAt).toLocaleString('en-IE')],
                ['Completed', run.completedAt ? new Date(run.completedAt).toLocaleString('en-IE') : '—'],
                ['Processed', run.staffProcessed?.toLocaleString() || '0'],
                ['Errors',    run.staffErrors || '0'],
                ['By',        run.triggeredBy?.name || '—'],
              ].map(([l, v]) => (
                <Col xs={6} sm={3} key={l}>
                  <div className="text-muted" style={{ fontSize: '.72rem' }}>{l}</div>
                  <div className="fw-semibold" style={{ fontSize: '.82rem' }}>{v}</div>
                </Col>
              ))}
            </Row>
            {run.log ? (
              <div style={{ background: '#1a1a2e', color: '#e6edf3', padding: '12px 14px', borderRadius: 6, maxHeight: 420, overflowY: 'auto', fontFamily: 'monospace', fontSize: '.74rem', lineHeight: 1.5 }}>
                {run.log.split('\n').map((line, i) => (
                  <div key={i} style={{ color: line.includes('ERROR') ? '#ff7b72' : line.includes('Done') ? '#3fb950' : '#e6edf3' }}>
                    {line}
                  </div>
                ))}
              </div>
            ) : (
              <Alert variant="secondary" className="mb-0">No log available for this run.</Alert>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function RunHistoryScreen() {
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page,         setPage]         = useState(1);
  const [viewRunId,    setViewRunId]    = useState(null);

  const queryParams = {
    page,
    ...(typeFilter   && { type: typeFilter }),
    ...(statusFilter && { status: statusFilter }),
  };
  const { data, isLoading, isError } = useGetRunHistoryQuery(queryParams);
  const runs  = data?.runs  || [];
  const pages = data?.pages || 1;

  if (isLoading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  if (isError)   return <Alert variant="danger" className="m-3">Failed to load run history.</Alert>;

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col><h4 className="mb-0">Calculator Run History</h4></Col>
      </Row>

      <Row className="g-2 mb-3">
        <Col xs="auto">
          <Form.Select size="sm" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <option value="">All types</option>
            <option value="batch">Batch only</option>
            <option value="individual">Individual only</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Select size="sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="error">Error</option>
            <option value="running">Running</option>
          </Form.Select>
        </Col>
      </Row>

      <div className="table-responsive">
        <Table bordered hover size="sm" style={{ fontSize: '.82rem' }}>
          <thead className="table-dark">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Staff / PPS</th>
              <th className="text-end">Year</th>
              <th className="text-end">Processed</th>
              <th className="text-end">Errors</th>
              <th>Status</th>
              <th>Duration</th>
              <th>By</th>
              <th style={{ width: 70 }}>Log</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={10} className="text-center text-muted py-3">No runs found</td></tr>
            )}
            {runs.map((r) => {
              const dur = r.completedAt && r.startedAt
                ? Math.round((new Date(r.completedAt) - new Date(r.startedAt)) / 1000)
                : null;
              return (
                <tr key={r._id}>
                  <td>{new Date(r.startedAt).toLocaleString('en-IE')}</td>
                  <td>
                    <Badge bg={r.runType === 'batch' ? 'dark' : 'secondary'} style={{ fontSize: '.65rem' }}>
                      {r.runType}
                    </Badge>
                    {r.runType === 'batch' && (
                      <Badge
                        bg={r.resumed ? 'warning' : 'light'}
                        text={r.resumed ? 'dark' : 'muted'}
                        style={{ fontSize: '.65rem', marginLeft: 4 }}
                      >
                        {r.resumed ? 'resumed' : 'fresh'}
                      </Badge>
                    )}

                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '.75rem' }}>
                    {r.runType === 'individual' ? `${r.staffName} (${r.ppsNumber})` : <span className="text-muted">All staff</span>}
                  </td>
                  <td className="text-end">{r.runYear}</td>
                  <td className="text-end">{r.staffProcessed?.toLocaleString() || '—'}</td>
                  <td className="text-end" style={{ color: r.staffErrors > 0 ? '#dc3545' : undefined }}>
                    {r.staffErrors || 0}
                  </td>
                  <td>
                    <Badge bg={r.status === 'completed' ? 'success' : r.status === 'error' ? 'danger' : 'warning'}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="text-muted">
                    {dur !== null ? `${dur}s` : '—'}
                  </td>
                  <td>{r.triggeredBy?.name || '—'}</td>
                  <td>
                    <Button size="sm" variant="outline-secondary" style={{ fontSize: '.72rem', padding: '1px 6px' }}
                      onClick={() => setViewRunId(r._id)}>
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination size="sm">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
            {Array.from({ length: Math.min(pages, 8) }, (_, i) => i + 1).map((p) => (
              <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
            ))}
            <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}

      <LogModal runId={viewRunId} onClose={() => setViewRunId(null)} />
    </Container>
  );
}